import { apiFetch } from "./client";

// ── Types ──

export interface AccountBalanceResp {
  /** 账户总余额(分) */
  totalBalance: number;
  /** 现金余额(分) */
  cashBalance: number;
  /** 赠送余额(分) */
  giftedBalance: number;
  /** 累计充值(分) */
  totalRecharged: number;
  /** 累计消费(分) */
  totalConsumed: number;
  /** 累计赠送(分) */
  totalGifted: number;
}

// ── API ──

export function getAccountBalance(): Promise<AccountBalanceResp> {
  return apiFetch<AccountBalanceResp>("/discourse/api/v1/wallet/balance");
}

// ── Bill Overview ──

export interface BillOverviewResp {
  /** 累计消费(分) */
  totalConsumed: number;
  /** Token 历史消耗总量 */
  totalTokens: number;
  /** 内容缓存 Token */
  cachedTokens: number;
  /** 未命中缓存 Token */
  uncachedTokens: number;
  /** 输出 Token */
  outputTokens: number;
}

export function getBillOverview(): Promise<BillOverviewResp> {
  return apiFetch<BillOverviewResp>("/discourse/api/v1/wallet/bill/overview");
}

// ── Usage Detail (月度消费账单图表) ──

export interface DailyConsumption {
  /** 日期，如 05-01 */
  date: string;
  /** 消费金额(分) */
  amount: number;
  /** Token 消耗 */
  tokens: number;
  /** 请求次数 */
  requests: number;
}

export interface UsageDetailResp {
  /** 当月消费总额(分) */
  totalAmount: number;
  /** 当月 Token 消耗 */
  totalTokens: number;
  /** 当月请求次数 */
  totalRequests: number;
  /** 日消费列表 */
  dailyConsumption: DailyConsumption[];
}

export function getUsageDetail(month: string): Promise<UsageDetailResp> {
  return apiFetch<UsageDetailResp>("/discourse/api/v1/wallet/bill/usage", {
    params: { month },
  });
}

// ── Token Usage (月度Token消耗图表) ──

export interface TokenUsageResp {
  /** 当月 Token 总消耗 */
  totalTokens: number;
  /** 输入(内容缓存) */
  inputCached: DailyConsumption[];
  /** 输入(未命中缓存) */
  inputUncached: DailyConsumption[];
  /** 输出 */
  output: DailyConsumption[];
}

export function getTokenUsage(month: string): Promise<TokenUsageResp> {
  return apiFetch<TokenUsageResp>("/discourse/api/v1/wallet/bill/tokens", {
    params: { month },
  });
}

// ── Request Count (月度请求次数图表) ──

export interface RequestCountResp {
  /** 当月请求总次数 */
  totalRequests: number;
  /** 日请求次数列表 */
  dailyRequests: DailyConsumption[];
}

export function getRequestCount(month: string): Promise<RequestCountResp> {
  return apiFetch<RequestCountResp>("/discourse/api/v1/wallet/bill/requests", {
    params: { month },
  });
}

// ── Recharge Summary (累计充值金额) ──

export interface RechargeSummaryResp {
  /** 累计充值(分) */
  totalRecharged: number;
}

export function getRechargeSummary(): Promise<RechargeSummaryResp> {
  return apiFetch<RechargeSummaryResp>(
    "/discourse/api/v1/wallet/recharge/summary",
  );
}

// ── Transaction List (充值明细列表) ──

export interface TransactionItem {
  /** 记录ID */
  id: number;
  /** 交易流水号 */
  transactionNo: string;
  /** 关联订单号 */
  orderNo: string;
  /** 交易类型: 1=充值 2=赠送 3=消费 4=退款 */
  tradeType: number;
  /** 金额类型: 1=现金 2=赠送 */
  amountType: number;
  /** 交易金额(分) */
  amount: number;
  /** 交易状态: 0=失败 1=成功 2=处理中 */
  status: number;
  /** 赠送来源 */
  giftSource: string;
  /** 赠送余额过期时间 */
  expiresAt: string;
  /** 备注 */
  remark: string;
  /** 支付方式 */
  payWay: string;
  /** 交易时间 */
  createdAt: string;
}

export interface TransactionListParams {
  page: number;
  pageSize: number;
  tradeType?: number;
  startDate?: string;
  endDate?: string;
}

export interface TransactionListResp {
  /** 总条数 */
  total: number;
  /** 累计充值(分) */
  totalRecharged: number;
  /** 交易列表 */
  list: TransactionItem[];
}

export function getTransactionList(
  params: TransactionListParams,
): Promise<TransactionListResp> {
  return apiFetch<TransactionListResp>(
    "/discourse/api/v1/wallet/transactions",
    {
      params: {
        page: params.page,
        pageSize: params.pageSize,
        tradeType: params.tradeType ?? 0,
        startDate: params.startDate,
        endDate: params.endDate,
      },
    },
  );
}

// ── Claim Benefit (领取权益) ──

export interface ClaimedBenefitItem {
  /** 奖励名称 */
  name: string;
  /** 领取金额(分) */
  amount: number;
  /** 奖品类型 */
  rewardType: string;
  /** 触发方式标识 */
  triggerKey: string;
  /** 触发方式名称 */
  triggerName: string;
  /** 使用场景 */
  scene: string;
  /** 有效天数 */
  expireDays: number;
}

export interface ClaimBenefitResp {
  /** 本次领取成功的权益列表 */
  list: ClaimedBenefitItem[];
}

export function claimBenefit(): Promise<ClaimBenefitResp> {
  return apiFetch<ClaimBenefitResp>("/discourse/api/v1/wallet/claim-benefit", {
    method: "POST",
  });
}

// ── Quota Self Data (new-api-main /api/data/self) ──

/** new-api-main QuotaData 记录 */
export interface QuotaDataItem {
  id: number;
  user_id: number;
  username: string;
  model_name: string;
  created_at: number; // unix 秒
  token_used: number;
  count: number;
  quota: number;
}

/** getQuotaSelfData 参数 */
export interface QuotaSelfDataParams {
  start_timestamp: number;
  end_timestamp: number;
}

/**
 * 获取当前用户的模型调用量数据（/api/data/self）
 *
 * 对应 new-api-main 的 GetUserQuotaDates 控制器，
 * 返回 QuotaData 数组，每条包含 model_name / quota / count / token_used / created_at。
 */
export function getQuotaSelfData(
  params: QuotaSelfDataParams,
): Promise<QuotaDataItem[]> {
  return apiFetch<QuotaDataItem[]>("/api/data/self", {
    params: {
      start_timestamp: params.start_timestamp,
      end_timestamp: params.end_timestamp,
    },
  });
}

// ── Model Data (透传 new-api 用量数据) /discourse/api/v1/model/data ──

/** new-api 用量数据记录 */
export interface NewapiQuotaDataItem {
  id: number;
  user_id: number;
  created_at: number; // unix 秒
  token_used: number;
  count: number;
  quota: number;
}

export interface NewapiQuotaDataResp {
  items: NewapiQuotaDataItem[];
}

export interface NewapiQuotaDataParams {
  start_timestamp: number;
  end_timestamp: number;
  default_time: "hour" | "day" | "week";
}

export function getModelData(
  params: NewapiQuotaDataParams,
): Promise<NewapiQuotaDataResp> {
  return apiFetch<NewapiQuotaDataResp>("/discourse/api/v1/model/data", {
    params: {
      start_timestamp: params.start_timestamp,
      end_timestamp: params.end_timestamp,
      default_time: params.default_time,
    },
  });
}

// ── Model Log (透传 new-api 日志查询) /discourse/api/v1/model/log ──

/** new-api 日志记录 */
export interface NewapiLogItem {
  id: number;
  user_id: number;
  created_at: number; // unix 秒
  type: number; // 1=充值 2=消费 3=管理 4=系统 5=错误 6=退款
  content: string;
  username: string;
  token_name: string;
  model_name: string;
  quota: number;
  prompt_tokens: number;
  completion_tokens: number;
  use_time: number; // 毫秒
  is_stream: boolean;
  channel: number;
  channel_name: string;
  token_id: number;
  group: string;
  ip: string;
  request_id: string;
  upstream_request_id: string;
  other: string;
}

export interface NewapiLogResp {
  page: number;
  page_size: number;
  total: number;
  items: NewapiLogItem[];
}

export interface NewapiLogParams {
  p: number;
  page_size: number;
  start_timestamp?: number;
  end_timestamp?: number;
}

export function getModelLog(
  params: NewapiLogParams,
): Promise<NewapiLogResp> {
  return apiFetch<NewapiLogResp>("/discourse/api/v1/model/log", {
    params: {
      p: params.p,
      page_size: params.page_size,
      start_timestamp: params.start_timestamp,
      end_timestamp: params.end_timestamp,
    },
  });
}

// ── Wallet Recharge (钱包充值) ──

/** 充值支付方式 */
export type RechargePayWay = "ALI_OC" | "WX_NATIVE";

/** 充值请求 */
export interface RechargeReq {
  /** 充值金额(分), 最小100(1元), 最大500000(5000元) */
  amount: number;
  /** 支付方式 */
  payWay: RechargePayWay;
}

/** 充值响应 */
export interface RechargeResp {
  /** 充值单号 */
  orderNo: string;
  /** 支付订单号 */
  paymentNo: string;
  /** 支付参数(前端调起支付用) */
  payData: string;
  /** 支付参数类型: codeUrl/codeImgUrl/payUrl/form/jsBridge */
  payDataType: string;
  /** 过期时间 */
  expiredAt: string;
}

/**
 * 发起钱包充值
 *
 * POST /discourse/api/v1/wallet/recharge
 */
export function createRecharge(req: RechargeReq): Promise<RechargeResp> {
  return apiFetch<RechargeResp>("/discourse/api/v1/wallet/recharge", {
    method: "POST",
    body: req,
  });
}
